<script>
    import { createEventDispatcher } from 'svelte';
    import AccessibleModal from '../shared/AccessibleModal.svelte';
    import { settingsStore, claimsSettings } from '../../stores/settingsStore';
    
    const dispatch = createEventDispatcher();
    
    export let show = false;
    
    function closeModal() {
        show = false;
        dispatch('close');
    }
    
    function handleToggleAutoNumberSubitems() {
        console.log('[SettingsModal] Toggle clicked, current value:', $claimsSettings.autoNumberSubitems);
        settingsStore.toggleSetting('claims.autoNumberSubitems');
        console.log('[SettingsModal] After toggle, new value:', $claimsSettings.autoNumberSubitems);
    }
    
    function handleToggleAutoUpdateDependencies() {
        console.log('[SettingsModal] Toggle clicked, current value:', $claimsSettings.autoUpdateDependencies);
        settingsStore.toggleSetting('claims.autoUpdateDependencies');
        console.log('[SettingsModal] After toggle, new value:', $claimsSettings.autoUpdateDependencies);
    }
</script>

<AccessibleModal 
    bind:show={show}
    variant="neutral"
    title="Settings"
    titleId="settings-modal-title"
    on:close={closeModal}
    data-testid="settings-modal"
>
    <div class="modal-body">
        <div class="settings-content">
            <!-- Claims Settings Section -->
            <div class="settings-section">
                <h3 class="section-title">Claims</h3>
                
                <div class="setting-item">
                    <div class="setting-row">
                        <div class="setting-info">
                            <label for="autonumber-subitems" class="setting-label">
                                Auto-number subitems
                            </label>
                            <p class="setting-description">
                                Automatically continue numbering for (a), (b), (c) and (i), (ii), (iii) when pressing Enter
                            </p>
                        </div>
                        <label class="toggle-switch">
                            <input 
                                type="checkbox" 
                                id="autonumber-subitems"
                                checked={$claimsSettings.autoNumberSubitems}
                                on:change={handleToggleAutoNumberSubitems}
                            />
                            <span class="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                
                <div class="setting-item">
                    <div class="setting-row">
                        <div class="setting-info">
                            <label for="auto-update-dependencies" class="setting-label">
                                Auto-update claim dependencies
                            </label>
                            <p class="setting-description">
                                Automatically apply suggested claim dependencies when refreshing terms
                            </p>
                        </div>
                        <label class="toggle-switch">
                            <input 
                                type="checkbox" 
                                id="auto-update-dependencies"
                                checked={$claimsSettings.autoUpdateDependencies}
                                on:change={handleToggleAutoUpdateDependencies}
                            />
                            <span class="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </div>
</AccessibleModal>

<style>
.modal-body {
    min-height: 200px;
    padding: var(--spacing-4);
}

.settings-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
}

.settings-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
}

.section-title {
    padding-bottom: var(--spacing-2);
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: 600;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
}

.setting-item {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
}

.setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-4);
}

.setting-info {
    flex: 1;
}

.setting-label {
    display: block;
    margin-bottom: var(--spacing-1);
    font-size: var(--font-size-base);
    font-weight: 500;
    color: var(--color-text-primary);
}

.setting-description {
    margin: 0;
    font-size: var(--font-size-sm);
    line-height: 1.4;
    color: var(--color-text-secondary);
}

/* Toggle Switch Styles */
.toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    cursor: pointer;
}

.toggle-switch input {
    width: 0;
    height: 0;
    opacity: 0;
}

.toggle-slider {
    position: absolute;
    inset: 0;
    background-color: var(--color-toggle-off);
    border: 1px solid var(--color-border-default);
    border-radius: 24px;
    transition: all 0.3s ease;
}

.toggle-slider::before {
    position: absolute;
    bottom: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background-color: white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease;
    content: "";
}

.toggle-switch input:checked + .toggle-slider {
    background-color: var(--color-toggle-on);
    border-color: var(--color-toggle-on);
}

.toggle-switch input:checked + .toggle-slider::before {
    transform: translateX(20px);
}

.toggle-switch:hover input:checked + .toggle-slider {
    background-color: var(--color-toggle-on-hover);
    border-color: var(--color-toggle-on-hover);
}

/* Focus styles for accessibility */
.toggle-switch input:focus + .toggle-slider {
    box-shadow: 0 0 0 2px var(--color-focus-ring);
}

/* The modal body inherits the neutral variant theming from AccessibleModal */
</style>